"use client";

import { useEffect, useRef, useState } from "react";

type Status = "idle" | "submitting" | "success" | "error";

const NAME_MAX = 200;
const EMAIL_MAX = 320;
const SUBJECT_MAX = 150;
const MESSAGE_MIN = 10;
const MESSAGE_MAX = 5000;
const COOLDOWN_MS = 5000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputClass =
  "w-full bg-bg-alt/30 border border-border-thin px-4 py-3 text-off placeholder-silver/40 focus:border-blue focus:outline-none transition-colors duration-200";

const labelClass =
  "font-mono text-xs tracking-[0.2em] uppercase text-silver/90 mb-2 block";

type ContactFormProps = {
  initialSubject?: string;
};

export function ContactForm({ initialSubject }: ContactFormProps = {}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState(
    initialSubject ? initialSubject.slice(0, SUBJECT_MAX) : "",
  );
  const [message, setMessage] = useState("");
  // Honeypot field. Must stay empty for a real submission to go through.
  // Bots fill it; we silently drop those server-side.
  const [website, setWebsite] = useState("");

  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Cooldown is anti-double-click insurance after a successful send. It
  // clears on a 5s timer OR the first time the user edits any field
  // again, whichever comes first.
  const [cooldown, setCooldown] = useState(false);
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
    };
  }, []);

  const releaseCooldown = () => {
    setCooldown(false);
    if (cooldownTimerRef.current) {
      clearTimeout(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    }
  };

  // Wrap each setter so that editing any field after a successful send
  // clears the cooldown immediately. The 5s timer is just the upper
  // bound on the protection window.
  const onFieldChange = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    if (cooldown) releaseCooldown();
    if (status === "success") setStatus("idle");
    if (errorMessage) setErrorMessage(null);
  };

  const fieldsValid =
    name.trim().length > 0 &&
    name.trim().length <= NAME_MAX &&
    email.trim().length > 0 &&
    email.trim().length <= EMAIL_MAX &&
    EMAIL_RE.test(email.trim()) &&
    subject.trim().length > 0 &&
    subject.trim().length <= SUBJECT_MAX &&
    message.trim().length >= MESSAGE_MIN &&
    message.trim().length <= MESSAGE_MAX;

  const disabled = !fieldsValid || status === "submitting" || cooldown;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled) return;

    setStatus("submitting");
    setErrorMessage(null);

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject: subject.trim(),
          message: message.trim(),
          website,
        }),
      });

      const data = (await res.json().catch(() => null)) as
        | { ok: boolean; error?: string }
        | null;

      if (!res.ok || !data?.ok) {
        const fallback = "Could not send your message, please try again";
        setErrorMessage(data?.error || fallback);
        setStatus("error");
        return;
      }

      setStatus("success");
      setName("");
      setEmail("");
      setSubject("");
      setMessage("");
      setWebsite("");
      setCooldown(true);
      cooldownTimerRef.current = setTimeout(() => {
        setCooldown(false);
        cooldownTimerRef.current = null;
      }, COOLDOWN_MS);
    } catch {
      setErrorMessage("Could not send your message, please try again");
      setStatus("error");
    }
  };

  const submitLabel =
    status === "submitting"
      ? "Sending..."
      : status === "success"
        ? "Sent"
        : "Send message";

  return (
    <form onSubmit={handleSubmit} noValidate className="mt-10 space-y-6">
      {status === "success" && (
        <div
          role="status"
          aria-live="polite"
          className="border-l-2 border-blue px-4 py-3 text-sm leading-relaxed text-silver/90"
        >
          Thanks, your message is on its way. I&apos;ll get back to you when I
          can.
        </div>
      )}
      {status === "error" && errorMessage && (
        <div
          role="alert"
          aria-live="assertive"
          className="border-l-2 border-orange px-4 py-3 text-sm leading-relaxed text-silver/90"
        >
          {errorMessage}
        </div>
      )}

      <div>
        <label htmlFor="contact-name" className={labelClass}>
          Name
        </label>
        <input
          id="contact-name"
          type="text"
          autoComplete="name"
          required
          maxLength={NAME_MAX}
          value={name}
          onChange={(e) => onFieldChange(setName)(e.target.value)}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="contact-email" className={labelClass}>
          Email
        </label>
        <input
          id="contact-email"
          type="email"
          autoComplete="email"
          required
          maxLength={EMAIL_MAX}
          value={email}
          onChange={(e) => onFieldChange(setEmail)(e.target.value)}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="contact-subject" className={labelClass}>
          Subject
        </label>
        <input
          id="contact-subject"
          type="text"
          required
          maxLength={SUBJECT_MAX}
          placeholder="e.g. Suggestion about Ch.3 LQG"
          value={subject}
          onChange={(e) => onFieldChange(setSubject)(e.target.value)}
          className={inputClass}
        />
      </div>

      <div>
        <label htmlFor="contact-message" className={labelClass}>
          Message
        </label>
        <textarea
          id="contact-message"
          required
          minLength={MESSAGE_MIN}
          maxLength={MESSAGE_MAX}
          rows={8}
          value={message}
          onChange={(e) => onFieldChange(setMessage)(e.target.value)}
          className={`${inputClass} resize-y min-h-[12rem]`}
        />
        <p className="mt-2 font-mono text-xs text-silver/80">
          {message.trim().length} / {MESSAGE_MAX}
        </p>
      </div>

      {/* Honeypot. Off-screen and aria-hidden so humans never see or focus
          it; bots fill it and the server silently drops their submission. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-9999px",
          width: "1px",
          height: "1px",
          overflow: "hidden",
        }}
      >
        <label htmlFor="contact-website">Website</label>
        <input
          id="contact-website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      <div className="pt-2">
        <button
          type="submit"
          disabled={disabled}
          className="inline-flex items-center gap-2 border border-border-strong px-5 py-2.5 font-mono text-xs tracking-[0.2em] uppercase text-off hover:bg-bg-alt/70 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
        >
          {submitLabel}
        </button>
      </div>
    </form>
  );
}
